/**
 * Maps a BE error_code from the /api/cloud/* namespace to a frontend
 * i18n key under `dataCloud.backendErrors.*`.
 *
 * Mirrors the W9 adjustmentsBackendErrorMapper pattern. Used by all hooks
 * + DataCloudPage render layer to translate BE errors at render time so
 * language switches re-translate.
 *
 * Contract: BE emits `{error, error_code, suggestions}`. `error` is
 * auto-English. `suggestions[0]` is localized BE text. FE mapper prefers
 * i18n key when code is known; falls back to suggestion (localized) when
 * unknown; falls back to message when no suggestion.
 */

export const KNOWN_CLOUD_BACKEND_ERROR_CODES = [
  // Inherited LoadDataException codes (W6/W7/W8 shared)
  'VALIDATION_ERROR',
  'MISSING_PARAMETER',
  'INVALID_PARAMETER',
  'DELIMITER_MISMATCH',
  'INVALID_COLUMN_INDEX',
  'PARSING_ERROR',
  'CSV_PARSING_ERROR',
  'DATETIME_PARSING_ERROR',
  'ENCODING_ERROR',
  'UPLOAD_ERROR',
  'CHUNK_UPLOAD_ERROR',
  'UPLOAD_NOT_FOUND',
  'INCOMPLETE_UPLOAD',
  'TIMEZONE_ERROR',
  'UNSUPPORTED_TIMEZONE',
  'TIMEZONE_CONVERSION_ERROR',
  'LOAD_DATA_ERROR',
  // Cloud-specific codes (W10 T1)
  'CLOUD_ERROR',
  'TIMESTAMP_MISMATCH',
  'INSUFFICIENT_MATCHING_POINTS',
  'COLUMN_NOT_FOUND',
  'TOLERANCE_BOUNDS_EMPTY',
  'UNKNOWN_TOLERANCE_TYPE',
  'INVALID_UPLOAD_ID',
  // Generic cloud codes (W10 T2 + T3)
  'DUPLICATE_TIMESTAMPS',
  'NO_NUMERIC_DATA',
  'NO_DATA_AFTER_COMBINE',
  'NAN_IN_PROCESSED_DATA',
  'NO_NUMERIC_DATA_TARGET',
  'NO_NUMERIC_DATA_PREDICTOR',
  'FILE_TOO_LARGE',
  'TOO_MANY_ROWS',
  'TOO_MANY_COLUMNS',
  'TOO_FEW_COLUMNS',
  // Live backend codes (reconciled against backend cloud.py — W10)
  'BAD_REQUEST',
  'DATETIME_ERROR',
  'MISSING_UTC_COLUMN',
  'MISSING_LOAD_COLUMN',
  'INSUFFICIENT_DATA',
  'INVALID_PARAMS',
  'MISSING_FILE',
  'INVALID_CHUNK_INDEX',
  'MISSING_UPLOAD_ID',
  'INVALID_FILE_TYPE',
  'FORBIDDEN',
  'PROCESSING_ERROR',
  'INTERNAL_ERROR',
  // Subscription-layer quota code (defense-in-depth; normally handled via the upgrade modal)
  'compute_hours_exhausted',
] as const;

export type CloudBackendErrorCode =
  (typeof KNOWN_CLOUD_BACKEND_ERROR_CODES)[number];

export interface CloudBackendErrorInput {
  code: string | undefined;
  message: string | undefined;
  /** Preferred over `message` for fallback since BE-localized. Pass `suggestions[0]`. */
  suggestion?: string | undefined;
}

export interface CloudBackendErrorMapping {
  translationKey: string | null;
  fallbackMessage: string;
}

const KNOWN_SET = new Set<string>(KNOWN_CLOUD_BACKEND_ERROR_CODES);

export function mapCloudBackendError(
  input: CloudBackendErrorInput,
): CloudBackendErrorMapping {
  const fallbackMessage = input.suggestion ?? input.message ?? '';
  if (input.code && KNOWN_SET.has(input.code)) {
    return {
      translationKey: `backendErrors.${input.code}`,
      fallbackMessage,
    };
  }
  return { translationKey: null, fallbackMessage };
}
